/**
 * Bulk edit selected courses: category, instructor, status (Draft / Publish / Archive),
 * visibility, price, tags. Fields that do not apply to the selection are disabled with a reason.
 * Flow: pick fields -> confirmation (dry run shows how many will change) -> apply (audited).
 * Backend: /api/admin/courses/bulk/edit/applicability, /api/admin/courses/bulk/edit
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Pencil, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const FIELDS = [
  { key: 'status', label: 'Status' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'category', label: 'Category' },
  { key: 'instructor_name', label: 'Instructor' },
  { key: 'price', label: 'Price (ZAR)' },
  { key: 'tags', label: 'Tags' },
];

export default function CourseBulkEditDialog({ token, courseIds, categories = [], initialChanges = {}, onClose, onDone }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [applicability, setApplicability] = useState(null);
  const [enabled, setEnabled] = useState(() => Object.fromEntries(Object.keys(initialChanges).map((k) => [k, true])));
  const [values, setValues] = useState({ status: 'draft', visibility: 'public', category: '', instructor_name: '', price: '', tags_mode: 'add', tags: '', ...initialChanges });
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    axios.post(`${API}/admin/courses/bulk/edit/applicability`, { course_ids: courseIds }, { headers })
      .then((res) => setApplicability(res.data))
      .catch(() => toast.error('Could not check the selection'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIds.join(',')]);

  const fieldInfo = (key) => applicability?.fields?.[key === 'status' && values.status === 'published' ? 'status_publish' : key] || applicability?.fields?.[key];

  const buildChanges = () => {
    const c = {};
    if (enabled.status) c.status = values.status;
    if (enabled.visibility) c.visibility = values.visibility;
    if (enabled.category) c.category = values.category;
    if (enabled.instructor_name) c.instructor_name = values.instructor_name;
    if (enabled.price) c.price = values.price === '' ? null : Number(values.price);
    if (enabled.tags) c.tags = { mode: values.tags_mode, values: values.tags.split(/[;,]/).map((t) => t.trim()).filter(Boolean) };
    return c;
  };

  const validate = (c) => {
    if (!Object.keys(c).length) return 'Choose at least one field to change';
    if ('category' in c && !c.category) return 'Choose a category';
    if ('price' in c && (c.price === null || Number.isNaN(c.price) || c.price < 0)) return 'Enter a valid price (0 or more)';
    if ('tags' in c && c.tags.mode !== 'replace' && !c.tags.values.length) return 'Enter at least one tag';
    return null;
  };

  const review = async () => {
    const changes = buildChanges();
    const err = validate(changes);
    if (err) { toast.error(err); return; }
    setWorking(true);
    try {
      const res = await axios.post(`${API}/admin/courses/bulk/edit`, { course_ids: courseIds, changes, dry_run: true }, { headers });
      setConfirm({ changes, ...res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not prepare the change');
    } finally {
      setWorking(false);
    }
  };

  const apply = async () => {
    setWorking(true);
    try {
      const res = await axios.post(`${API}/admin/courses/bulk/edit`, { course_ids: courseIds, changes: confirm.changes, dry_run: false }, { headers });
      toast.success(`${res.data.updated} course${res.data.updated === 1 ? '' : 's'} updated`);
      onDone && onDone(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk edit failed');
    } finally {
      setWorking(false);
    }
  };

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="course-bulk-edit-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-navigate-dark">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><Pencil className="h-5 w-5 text-navigate-gold" /> Bulk edit {courseIds.length} course{courseIds.length === 1 ? '' : 's'}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="bulk-edit-close"><X className="h-5 w-5" /></button>
        </div>

        {!applicability ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-navigate-gold" /></div>
        ) : confirm ? (
          <div className="p-5 space-y-4" data-testid="bulk-edit-confirm">
            <div className="p-4 rounded-xl border border-navigate-gold/40 bg-navigate-gold/10">
              <p className="text-white text-lg font-semibold" data-testid="bulk-edit-confirm-count">{confirm.will_change} of {confirm.selected} courses will change</p>
              <p className="text-sm text-zinc-300">Fields: {confirm.fields.join(', ')} · {confirm.unchanged} already have these values</p>
            </div>
            {confirm.skipped?.length > 0 && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200 space-y-1 max-h-40 overflow-y-auto" data-testid="bulk-edit-skipped">
                <p className="font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Some changes will not be applied:</p>
                {confirm.skipped.map((s) => (
                  <div key={s.course_id}>{s.title}: {s.skipped_fields.map((f) => `${f.field} - ${f.reason}`).join('; ')}</div>
                ))}
              </div>
            )}
            {confirm.preview?.length > 0 && (
              <ul className="text-xs text-zinc-400 max-h-40 overflow-y-auto space-y-0.5">
                {confirm.preview.map((p) => <li key={p.course_id}>• {p.title} <span className="text-zinc-600">({p.changes.join(', ')})</span></li>)}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setConfirm(null)}>Back</Button>
              <Button onClick={apply} disabled={working || confirm.will_change === 0} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="bulk-edit-apply-btn">
                {working && <Loader2 className="h-4 w-4 animate-spin" />} Apply to {confirm.will_change} course{confirm.will_change === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {FIELDS.map(({ key, label }) => {
              const info = fieldInfo(key);
              const disabled = info && !info.enabled;
              return (
                <div key={key} className={`p-3 rounded-xl border ${enabled[key] && !disabled ? 'border-navigate-gold/40 bg-navigate-surface' : 'border-zinc-800'} ${disabled ? 'opacity-50' : ''}`} data-testid={`bulk-field-${key}`}>
                  <label className="flex items-center gap-2 text-sm text-white">
                    <input type="checkbox" disabled={disabled} checked={!!enabled[key] && !disabled} onChange={(e) => setEnabled((s) => ({ ...s, [key]: e.target.checked }))} data-testid={`bulk-field-${key}-toggle`} />
                    {label}
                    {info && info.applicable < info.total && info.enabled && <span className="text-xs text-amber-300 ml-auto">applies to {info.applicable} of {info.total}</span>}
                    {disabled && <span className="text-xs text-zinc-400 ml-auto" data-testid={`bulk-field-${key}-disabled-reason`}>Not available: {info.reasons?.[0]?.reason}</span>}
                  </label>
                  {enabled[key] && !disabled && (
                    <div className="mt-2">
                      {key === 'status' && (
                        <select value={values.status} onChange={(e) => set('status', e.target.value)} className="h-9 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="bulk-status-value">
                          <option value="draft">Set to Draft</option>
                          <option value="published">Publish</option>
                          <option value="private">Set to Private (admins only)</option>
                          <option value="archived">Archive</option>
                        </select>
                      )}
                      {key === 'status' && values.status === 'published' && applicability.publish_blocked_no_content > 0 && (
                        <p className="text-xs text-amber-300 mt-1">{applicability.publish_blocked_no_content} selected course(s) have no content and will not be published.</p>
                      )}
                      {key === 'status' && values.status !== 'published' && (
                        <p className="text-xs text-zinc-500 mt-1">Learners lose access immediately; enrolled learners keep their progress and see a “temporarily unavailable” message.</p>
                      )}
                      {key === 'visibility' && (
                        <select value={values.visibility} onChange={(e) => set('visibility', e.target.value)} className="h-9 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="bulk-visibility-value">
                          <option value="public">Public (everyone)</option>
                          <option value="organisation">Organisation only (owning + assigned orgs)</option>
                          <option value="hidden">Hidden (direct link only)</option>
                        </select>
                      )}
                      {key === 'category' && (
                        <>
                          <Input list="bulk-categories" value={values.category} onChange={(e) => set('category', e.target.value)} placeholder="Category" className="bg-zinc-900 border-zinc-700 text-white" data-testid="bulk-category-value" />
                          <datalist id="bulk-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                        </>
                      )}
                      {key === 'instructor_name' && (
                        <Input value={values.instructor_name} onChange={(e) => set('instructor_name', e.target.value)} placeholder="Instructor name (leave empty to clear)" className="bg-zinc-900 border-zinc-700 text-white" data-testid="bulk-instructor-value" />
                      )}
                      {key === 'price' && (
                        <Input type="number" min="0" step="0.01" value={values.price} onChange={(e) => set('price', e.target.value)} placeholder="0 = free" className="bg-zinc-900 border-zinc-700 text-white w-40" data-testid="bulk-price-value" />
                      )}
                      {key === 'tags' && (
                        <div className="flex gap-2">
                          <select value={values.tags_mode} onChange={(e) => set('tags_mode', e.target.value)} className="h-9 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="bulk-tags-mode">
                            <option value="add">Add</option>
                            <option value="remove">Remove</option>
                            <option value="replace">Replace all with</option>
                          </select>
                          <Input value={values.tags} onChange={(e) => set('tags', e.target.value)} placeholder="tag1; tag2" className="bg-zinc-900 border-zinc-700 text-white" data-testid="bulk-tags-value" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={onClose}>Cancel</Button>
              <Button onClick={review} disabled={working} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="bulk-edit-review-btn">
                {working && <Loader2 className="h-4 w-4 animate-spin" />} Review changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
